import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/Button';
import { Badge } from './ui/badge';
import { InsuranceInfo } from './InsuranceInfo';
import { 
  Heart, 
  Video, 
  Calendar, 
  Shield, 
  Phone, 
  CheckCircle, 
  Star,
  Clock,
  Brain,
  Users,
  Stethoscope,
  FileText,
  CreditCard,
  HeadphonesIcon,
  MessageCircle,
  UserCheck,
  Zap,
  Award,
  Lock,
  ArrowRight,
  Play,
  MapPin
} from 'lucide-react';

const services = [
  {
    title: "Individual Therapy",
    description: "One-on-one sessions with licensed therapists",
    icon: Heart,
    features: ["Cognitive Behavioral Therapy (CBT)", "Dialectical Behavior Therapy (DBT)", "Psychodynamic Therapy", "Trauma-Focused Therapy"],
    duration: "45-60 minutes",
    availability: "In-person & Telehealth"
  },
  {
    title: "Psychiatric Evaluation",
    description: "Comprehensive mental health assessment and diagnosis",
    icon: Brain,
    features: ["Diagnostic Assessment", "Treatment Planning", "Medication Evaluation", "Risk Assessment"],
    duration: "90 minutes",
    availability: "In-person & Telehealth"
  },
  {
    title: "Medication Management",
    description: "Expert psychiatric medication prescribing and monitoring",
    icon: Stethoscope,
    features: ["Medication Optimization", "Side Effect Management", "Drug Interaction Review", "Progress Monitoring"],
    duration: "30-45 minutes",
    availability: "In-person & Telehealth"
  },
  {
    title: "Crisis Intervention",
    description: "Immediate support during mental health crises",
    icon: HeadphonesIcon,
    features: ["24/7 Crisis Line", "Emergency Assessment", "Safety Planning", "Immediate Intervention"],
    duration: "As needed",
    availability: "24/7 Support"
  },
  {
    title: "Group Therapy",
    description: "Therapeutic groups for specific conditions and goals",
    icon: Users,
    features: ["Anxiety Support Groups", "Depression Recovery", "Addiction Recovery", "Skills Building"],
    duration: "90 minutes",
    availability: "In-person & Virtual"
  },
  {
    title: "Family Therapy",
    description: "Therapy sessions involving family members",
    icon: UserCheck,
    features: ["Family Systems Therapy", "Couples Counseling", "Parent-Child Therapy", "Communication Skills"],
    duration: "60-90 minutes",
    availability: "In-person & Telehealth"
  }
];

const paymentOptions = [
  {
    title: "Insurance Coverage",
    description: "We accept most major insurance plans",
    icon: Shield,
    features: [
      "Direct insurance billing",
      "Prior authorization assistance", 
      "Benefits verification",
      "Coverage maximization"
    ],
    note: "Most services covered with standard copays"
  },
  {
    title: "Self-Pay Options",
    description: "Competitive rates for uninsured patients",
    icon: CreditCard,
    features: [
      "Transparent pricing",
      "Payment plans available",
      "Sliding scale fees",
      "Financial assistance programs"
    ],
    note: "Ask about our financial assistance programs"
  },
  {
    title: "Employee Benefits",
    description: "Contracted with major EAP providers",
    icon: Award,
    features: [
      "Employee Assistance Programs",
      "Corporate wellness contracts",
      "Flexible Spending Accounts",
      "Health Savings Accounts"
    ],
    note: "Check with your employer about mental health benefits"
  }
];

const processSteps = [
  {
    step: 1,
    title: "Schedule Your Appointment",
    description: "Book online or call our scheduling team",
    icon: Calendar,
    details: "Choose from available times that work with your schedule"
  },
  {
    step: 2,
    title: "Complete Intake Forms",
    description: "Fill out your medical history and insurance information",
    icon: FileText,
    details: "Secure online forms save time during your visit"
  },
  {
    step: 3,
    title: "Attend Your Session",
    description: "Meet with your provider in-person or via telehealth",
    icon: Video,
    details: "Professional, confidential, and personalized care"
  },
  {
    step: 4,
    title: "Follow Your Treatment Plan",
    description: "Work with your provider to achieve your mental health goals",
    icon: CheckCircle,
    details: "Ongoing support and adjustments as needed"
  }
];

const telehealthBenefits = [
  {
    title: "Convenience & Accessibility",
    description: "Access care from the comfort of your home",
    icon: Clock,
    benefits: ["No travel time", "Flexible scheduling", "Reduced wait times", "Home comfort"]
  },
  {
    title: "Privacy & Discretion",
    description: "Maintain complete confidentiality",
    icon: Lock,
    benefits: ["Private setting", "No waiting rooms", "Confidential platform", "Secure technology"]
  },
  {
    title: "Continuity of Care",
    description: "Never miss appointments due to circumstances",
    icon: Zap,
    benefits: ["Weather-independent", "Travel-friendly", "Consistent scheduling", "Reduced cancellations"]
  }
];

const qualityIndicators = [
  { label: "Board-Certified Providers", value: "100%" },
  { label: "Patient Satisfaction", value: "4.8/5" },
  { label: "Same-Day Availability", value: "85%" },
  { label: "Insurance Acceptance Rate", value: "95%" }
];

export const ForPatients = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-clinic-blue-50 to-clinic-gray-50">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="icon-container icon-container-2xl icon-medical">
                <Heart className="icon-4xl" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-clinic-gray-900 mb-6">
              Your Mental Health Journey Starts Here
            </h1>
            <p className="text-xl text-clinic-gray-600 max-w-3xl mx-auto mb-8">
              Compassionate, evidence-based psychiatric care tailored to your unique needs. 
              Take the first step toward better mental health with our expert team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="btn-xl" asChild>
                <Link to="/booking/telehealth">
                  <Calendar className="w-6 h-6 mr-3" />
                  Schedule Appointment
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="btn-xl" asChild>
                <Link to="/contact">
                  <Phone className="w-6 h-6 mr-3" />
                  Call (555) 123-4567
                </Link>
              </Button>
            </div>
          </div>

          {/* Quality Indicators */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {qualityIndicators.map((indicator, index) => (
              <Card key={index} className="text-center card-elevated">
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-clinic-blue-600 mb-2">
                    {indicator.value}
                  </div>
                  <p className="text-clinic-gray-600">{indicator.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-clinic-gray-900 mb-4">
              Comprehensive Mental Health Services
            </h2>
            <p className="text-xl text-clinic-gray-600 max-w-3xl mx-auto">
              From initial evaluation to ongoing treatment, we provide full-spectrum 
              psychiatric care to support your mental wellness journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="card-elevated card-interactive">
                <CardHeader>
                  <div className="icon-container icon-container-lg icon-medical mb-4">
                    <service.icon className="icon-2xl" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-clinic-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-3 border-t border-clinic-gray-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-clinic-gray-500">Duration:</span>
                        <span className="font-medium">{service.duration}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-clinic-gray-500">Available:</span>
                        <Badge variant="secondary">{service.availability}</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-clinic-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-clinic-gray-600 max-w-3xl mx-auto">
              Getting started with mental health care is simple. Follow these steps 
              to begin your journey to better mental wellness.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step) => (
              <Card key={step.step} className="text-center card-elevated">
                <CardContent className="p-6">
                  <div className="icon-container icon-container-xl icon-medical mx-auto mb-4">
                    <step.icon className="icon-2xl" />
                  </div>
                  <div className="bg-clinic-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{step.title}</h3>
                  <p className="text-clinic-gray-600 mb-3">{step.description}</p>
                  <p className="text-sm text-clinic-gray-500">{step.details}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Telehealth Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="icon-container icon-container-2xl icon-medical">
                <Video className="icon-3xl" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-clinic-gray-900 mb-4">
              Telehealth Services
            </h2>
            <p className="text-xl text-clinic-gray-600 max-w-3xl mx-auto">
              Access high-quality psychiatric care from anywhere with our secure, 
              HIPAA-compliant telehealth platform.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {telehealthBenefits.map((benefit, index) => (
              <Card key={index} className="card-elevated">
                <CardHeader>
                  <div className="icon-container icon-container-lg icon-medical mb-4">
                    <benefit.icon className="icon-2xl" />
                  </div>
                  <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  <CardDescription>{benefit.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {benefit.benefits.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-clinic-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Payment & Insurance Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-clinic-gray-900 mb-4">
              Payment Options & Insurance
            </h2>
            <p className="text-xl text-clinic-gray-600 max-w-3xl mx-auto">
              We believe mental health care should be accessible. We offer multiple 
              payment options and work with your insurance to minimize out-of-pocket costs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {paymentOptions.map((option, index) => (
              <Card key={index} className="card-elevated">
                <CardHeader>
                  <div className="icon-container icon-container-lg icon-medical mb-4">
                    <option.icon className="icon-2xl" />
                  </div>
                  <CardTitle className="text-xl">{option.title}</CardTitle>
                  <CardDescription>{option.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {option.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-clinic-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="p-3 bg-clinic-blue-50 rounded-lg">
                    <p className="text-xs text-clinic-blue-700">{option.note}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Insurance Details */}
          <InsuranceInfo />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-clinic-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Take the First Step?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Don't wait to prioritize your mental health. Schedule your appointment today 
            and start your journey toward better mental wellness.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="btn-xl" asChild>
              <Link to="/booking/telehealth">
                <Calendar className="w-6 h-6 mr-3" />
                Book Appointment Online
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="btn-xl border-white text-black hover:bg-white hover:text-clinic-blue-600" asChild>
              <Link to="/contact">
                <MessageCircle className="w-6 h-6 mr-3" />
                Chat with Us
              </Link>
            </Button>
          </div>
          <p className="mt-6 text-sm opacity-75">
            Available 24/7 for crisis support • (555) 123-4567
          </p>
        </div>
      </section>
    </div>
  );
};